import type { AttendeeFields } from '@/types/all-attendees.types';

export type ColumnDefinition = {
  id: string;
  label: string;
  /** Omit to disable sorting for this column. */
  sortKey?: string;
  /** AttendeeFields contribution when this column is enabled. Absent = base field, always fetched. */
  fields?: AttendeeFields;
  columnWidth: string;
  /** Whether the user can toggle this column off. */
  isRemovable: boolean;
  /** Present only for dynamic registration field columns. */
  registrationQuestion?: number;
};

export const COLUMN_DEFINITIONS: ColumnDefinition[] = [
  {
    id: 'full_name',
    label: 'Full Name',
    sortKey: 'profile.full_name',
    fields: { profile: ['full_name', 'avatar'] },
    columnWidth: '200px',
    isRemovable: false,
  },
  {
    id: 'email',
    label: 'Email',
    sortKey: 'email',
    columnWidth: '200px',
    isRemovable: false,
  },
  {
    id: 'job_title',
    label: 'Title / Role',
    sortKey: 'profile.job_title',
    fields: { profile: ['job_title'] },
    columnWidth: '150px',
    isRemovable: true,
  },
  {
    id: 'company_name',
    label: 'Company Name',
    sortKey: 'profile.company_name',
    fields: { profile: ['company_name'] },
    columnWidth: '150px',
    isRemovable: true,
  },
  {
    id: 'industry',
    label: 'Industry',
    sortKey: 'profile.industry',
    fields: { profile: ['industry'] },
    columnWidth: '150px',
    isRemovable: true,
  },
  {
    id: 'phone_number',
    label: 'Phone Number',
    sortKey: 'profile.phone_number',
    fields: { profile: ['phone_number'] },
    columnWidth: '150px',
    isRemovable: true,
  },
  {
    id: 'department',
    label: 'Department',
    sortKey: 'profile.department',
    fields: { profile: ['department'] },
    columnWidth: '150px',
    isRemovable: true,
  },
  {
    id: 'city',
    label: 'City',
    sortKey: 'location.city',
    fields: { location: ['city'] },
    columnWidth: '100px',
    isRemovable: true,
  },
  {
    id: 'state',
    label: 'State',
    sortKey: 'location.state',
    fields: { location: ['state'] },
    columnWidth: '100px',
    isRemovable: true,
  },
  {
    id: 'countryCode',
    label: 'Country',
    sortKey: 'location.countryCode',
    fields: { location: ['countryCode'] },
    columnWidth: '100px',
    isRemovable: true,
  },
  {
    id: 'timezone',
    label: 'Time Zone',
    sortKey: 'location.timezone',
    fields: { location: ['timezone'] },
    columnWidth: '100px',
    isRemovable: true,
  },
  {
    id: 'language',
    label: 'Preferred Language',
    sortKey: 'profile.preferred_language',
    fields: { profile: ['preferred_language'] },
    columnWidth: '190px',
    isRemovable: true,
  },
  {
    id: 'linkedIn',
    label: 'LinkedIn URL',
    sortKey: 'profile.linkedIn',
    fields: { profile: ['linkedIn'] },
    columnWidth: '150px',
    isRemovable: true,
  },
  {
    id: 'twitter',
    label: 'Twitter / X Handle',
    sortKey: 'profile.twitter',
    fields: { profile: ['twitter'] },
    columnWidth: '170px',
    isRemovable: true,
  },
  {
    id: 'total_registered',
    label: 'Total Events Registered',
    sortKey: 'stats.total_registered',
    fields: { stats: ['total_registered'] },
    columnWidth: '220px',
    isRemovable: true,
  },
  {
    id: 'total_viewed',
    label: 'Total Events Attended',
    sortKey: 'stats.total_viewed',
    fields: { stats: ['total_viewed'] },
    columnWidth: '210px',
    isRemovable: true,
  },
  {
    id: 'no_shows',
    label: 'No Show Count',
    sortKey: 'stats.no_shows',
    fields: { stats: ['no_shows'] },
    columnWidth: '150px',
    isRemovable: true,
  },
  {
    id: 'last_watch',
    label: 'Most Recent Event Date',
    sortKey: 'stats.last_watch',
    fields: { stats: ['last_watch'] },
    columnWidth: '220px',
    isRemovable: true,
  },
  {
    id: 'first_watch',
    label: 'First Event Date',
    sortKey: 'stats.first_watch',
    fields: { stats: ['first_watch'] },
    columnWidth: '200px',
    isRemovable: true,
  },
  {
    id: 'total_seconds',
    label: 'Lifetime Watch Time',
    sortKey: 'stats.total_seconds',
    fields: { stats: ['total_seconds'] },
    columnWidth: '200px',
    isRemovable: true,
  },
  {
    id: 'event_seconds',
    label: 'Watchtime',
    sortKey: 'event_data.total_seconds',
    fields: { event_data: ['total_seconds'] },
    columnWidth: '150px',
    isRemovable: true,
  },
  {
    id: 'source',
    label: 'Source',
    sortKey: 'event_data.source',
    fields: { event_data: ['source'] },
    columnWidth: '100px',
    isRemovable: true,
  },
  {
    id: 'attended',
    label: 'Attended',
    sortKey: 'event_data.attended',
    fields: { event_data: ['attended'] },
    columnWidth: '100px',
    isRemovable: true,
  },
  {
    id: 'attended_phase',
    label: 'Live vs. On-Demand',
    sortKey: 'event_data.attended_phase',
    fields: { event_data: ['attended_phase'] },
    columnWidth: '180px',
    isRemovable: true,
  },
  {
    id: 'created_date',
    label: 'Reg. Date',
    sortKey: 'event_data.created_date',
    fields: { event_data: ['created_date'] },
    columnWidth: '120px',
    isRemovable: true,
  },
  {
    id: 'referrer',
    label: 'Referral URL',
    sortKey: 'event_data.referrer',
    fields: { event_data: ['referrer'] },
    columnWidth: '140px',
    isRemovable: true,
  },
  {
    id: 'utm_source',
    label: 'UTM Source',
    fields: { event_data: ['utm_params'] },
    columnWidth: '120px',
    isRemovable: true,
  },
  {
    id: 'utm_medium',
    label: 'UTM Medium',
    fields: { event_data: ['utm_params'] },
    columnWidth: '120px',
    isRemovable: true,
  },
  {
    id: 'utm_campaign',
    label: 'UTM Campaign',
    fields: { event_data: ['utm_params'] },
    columnWidth: '140px',
    isRemovable: true,
  },
  {
    id: 'utm_content',
    label: 'UTM Content',
    fields: { event_data: ['utm_params'] },
    columnWidth: '120px',
    isRemovable: true,
  },
  {
    id: 'num_questions',
    label: 'Questions Asked',
    sortKey: 'event_data.num_questions',
    fields: { event_data: ['num_questions'] },
    columnWidth: '160px',
    isRemovable: true,
  },
  {
    id: 'num_comments',
    label: 'Chat Messages Sent',
    sortKey: 'event_data.num_comments',
    fields: { event_data: ['num_comments'] },
    columnWidth: '190px',
    isRemovable: true,
  },
  {
    id: 'num_reactions',
    label: 'Reactions / Emoji Count',
    sortKey: 'event_data.num_reactions',
    fields: { event_data: ['num_reactions'] },
    columnWidth: '220px',
    isRemovable: true,
  },
  {
    id: 'device_type',
    label: 'Device Type',
    sortKey: 'event_data.device_type',
    fields: { event_data: ['device_type'] },
    columnWidth: '120px',
    isRemovable: true,
  },
  {
    id: 'browser',
    label: 'Browser / App',
    sortKey: 'event_data.browser',
    fields: { event_data: ['browser'] },
    columnWidth: '140px',
    isRemovable: true,
  },
  {
    id: 'engagement_score',
    label: 'Engagement Score',
    sortKey: 'event_data.engagement_score',
    fields: { event_data: ['engagement_score'] },
    columnWidth: '180px',
    isRemovable: true,
  },
  {
    id: 'associated_campaigns',
    label: 'Associated Campaigns',
    sortKey: 'event_data.associated_campaigns',
    fields: { event_data: ['associated_campaigns'] },
    columnWidth: '200px',
    isRemovable: true,
  },
];

/** Default columns shown for standard (non-webinar) channels. */
export const DEFAULT_COLUMN_IDS_TOWNHALL: string[] = [
  'full_name',
  'email',
  'company_name',
  'job_title',
  'created_date',
  'attended',
  'watchtime',
];

/** Default columns shown for webinar channels. */
export const DEFAULT_COLUMN_IDS_WEBINAR: string[] = [
  'full_name',
  'email',
  'department',
  'job_title',
  'created_date',
  'attended',
  'watchtime',
];

/** Prefix used for dynamic registration field column IDs. */
export const REG_FIELD_ID_PREFIX = 'reg_';

/** Build a registration field column ID from its numeric question ID. */
export const regFieldId = (registrationQuestion: number) => `${REG_FIELD_ID_PREFIX}${registrationQuestion}`;

/** Merge enabled column field definitions into a single AttendeeFields object.
 *  Handles both static COLUMN_DEFINITIONS entries and dynamic reg_<n> entries. */
export function buildAttendeeFields(enabledIds: string[]): AttendeeFields {
  const result: AttendeeFields = {};

  // Static column definitions
  for (const def of COLUMN_DEFINITIONS) {
    if (!enabledIds.includes(def.id) || !def.fields) continue;
    const f = def.fields;
    if (f.profile) result.profile = [...(result.profile ?? []), ...f.profile];
    if (f.location) result.location = [...(result.location ?? []), ...f.location];
    if (f.stats) result.stats = [...(result.stats ?? []), ...f.stats];
    if (f.event_data) result.event_data = [...(result.event_data ?? []), ...f.event_data];
  }

  // Dynamic registration fields: 'reg_<registrationQuestion>' → numeric profile field
  for (const id of enabledIds) {
    if (!id.startsWith(REG_FIELD_ID_PREFIX)) continue;
    const n = Number(id.slice(REG_FIELD_ID_PREFIX.length));
    if (!Number.isNaN(n)) {
      result.profile = [...(result.profile ?? []), n];
    }
  }

  // Deduplicate — multiple columns can map to the same API field (e.g. all utm_* columns request utm_params)
  if (result.profile) result.profile = [...new Set(result.profile)];
  if (result.location) result.location = [...new Set(result.location)];
  if (result.stats) result.stats = [...new Set(result.stats)];
  if (result.event_data) result.event_data = [...new Set(result.event_data)];

  return result;
}
